// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import type { PropertyValues } from 'lit';
import { html, LitElement } from 'lit';
import { queryAssignedElements } from 'lit/decorators/query-assigned-elements.js';
import {
  useStyles,
  attachInternals,
  debounce,
  LogService,
  audit,
  GlobalStateService
} from '@nvidia-elements/core/internal';
import styles from './header.css?inline';
import { GridColumn } from '../column/column.js';
import type { Grid } from '../grid.js';
import { GridRow } from '../row/row.js';
import { GridCell } from '../cell/cell.js';

/**
 * @element nve-grid-header
 * @description Contains the column headers of a grid, managing column widths and providing structural context for the data rows below.
 * @since 0.11.0
 * @entrypoint \@nvidia-elements/core/grid
 * @slot - default slot for `nve-grid-column`
 * @cssprop --background
 * @cssprop --border-bottom
 * @cssprop --padding
 * @aria https://www.w3.org/WAI/ARIA/apg/patterns/grid/
 */
@audit()
export class GridHeader extends LitElement {
  static styles = useStyles([styles]);

  static readonly metadata = {
    tag: 'nve-grid-header',
    version: '0.0.0',
    children: [GridColumn.metadata.tag]
  };

  /** @private */
  declare _internals: ElementInternals;

  /** @private */
  @queryAssignedElements({
    selector: GridColumn.metadata.tag,
    flatten: true
  })
  columns!: GridColumn[];

  get #grid() {
    return this.parentElement as Grid;
  }

  render() {
    return html`
      <div internal-host>
        <slot @slotchange=${() => this.#computeColumnWidths()}></slot>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    attachInternals(this);
    this._internals.role = 'row';
    this.addEventListener('nve-grid-column-resize', this.#onColumnResize);
    this.#resizeObserver ??= new ResizeObserver(debounce(() => this.#computeColumnWidths(), 100));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('nve-grid-column-resize', this.#onColumnResize);
    this.#resizeObserver?.disconnect();
  }

  #onColumnResize = () => {
    void this.#computeColumnWidths();
  };

  #resizeObserver?: ResizeObserver;

  async firstUpdated(props: PropertyValues<this>) {
    super.firstUpdated(props);
    await this.updateComplete;
    this.#validateColumns();
  }

  async #computeColumnWidths() {
    await this.updateComplete;
    this.columns.forEach((c, i) => (c.ariaColIndex = `${i + 1}`));
    this.#grid.style.setProperty('--grid-auto-flow', 'initial');
    this.#grid.style.setProperty('--grid-template-column', this.columns.map((_, i) => `var(--c${i})`).join(' '));

    // compute initial column width
    this.columns.forEach((c, i) => this.#grid.style.setProperty(`--c${i}`, c.width ? c.width : `1fr`));

    // compute column width based on content
    await this.updateComplete;
    const widths = this.columns.map(c => (c.width ? c.width : `minmax(auto, ${c.getBoundingClientRect().width}px)`));
    widths.forEach((w, i) => this.#grid.style.setProperty(`--c${i}`, w));
  }

  #validateColumns() {
    if (GlobalStateService.state.env !== 'production') {
      const cells = this.#grid.querySelector(GridRow.metadata.tag)?.querySelectorAll(GridCell.metadata.tag);
      if (this.columns && cells && this.columns.length !== cells.length) {
        LogService.error(`grid-column (${this.columns.length}) and grid-cell (${cells.length}) count mismatch`);
      }
    }
  }
}
